import { getApiBaseUrl } from '@gto/api-client'
import { IS_PROD_ENV } from '@gto/helpers/env-checkers'
import { SAFE_PATH } from '@gto/helpers/safe-path'

import { gwAppUrl } from './gw-app-url'

// Each env has a separate client_id registered on the BE `oauth.Application`
// table. The values below are public client identifiers (OAuth-spec public,
// not secrets — PKCE replaces the client_secret).

const PROD_OAUTH_CLIENT_ID = 'LLT2QRRpRfi-InWDVHE1Z5ODCLdq7wHrmKra5bO8'
const DEV_OAUTH_CLIENT_ID = 'pFLDddzkkQxF76IH1VV7DT3SgWfNRuoXVUd7bMjp'

const SESSION_KEY_CODE_VERIFIER = 'gw_admin_oauth_code_verifier'
const SESSION_KEY_STATE = 'gw_admin_oauth_state'
const SESSION_KEY_FROM = 'gw_admin_oauth_from'
const SESSION_KEY_PROCESSED_CODE_PREFIX = 'gw_admin_oauth_processed_'
const OAUTH_REDIRECT_PATH = '/login/oauth'

export function getOAuthClientId(): string {
  return IS_PROD_ENV() ? PROD_OAUTH_CLIENT_ID : DEV_OAUTH_CLIENT_ID
}

function generateRandomBase64Url(byteLength: number): string {
  const bytes = crypto.getRandomValues(new Uint8Array(byteLength))

  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')
}

export async function computeCodeChallenge(codeVerifier: string): Promise<string> {
  const encoder = new TextEncoder()
  const data = encoder.encode(codeVerifier)
  const hash = await crypto.subtle.digest('SHA-256', data)

  return btoa(String.fromCharCode(...new Uint8Array(hash)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')
}

export async function buildOAuthRedirectUrl(from?: string): Promise<string> {
  const state = generateRandomBase64Url(32)
  const codeVerifier = generateRandomBase64Url(64)
  const codeChallenge = await computeCodeChallenge(codeVerifier)

  sessionStorage.setItem(SESSION_KEY_STATE, state)
  sessionStorage.setItem(SESSION_KEY_CODE_VERIFIER, codeVerifier)
  // Persist deep-link target so the callback loader can restore it after the
  // gto-wizard-web-vue round-trip. SAFE_PATH-validate even though the caller is expected
  // to have already done so — sessionStorage is the trust boundary.
  if (from !== undefined && SAFE_PATH.test(from)) {
    sessionStorage.setItem(SESSION_KEY_FROM, from)
  } else {
    sessionStorage.removeItem(SESSION_KEY_FROM)
  }

  // The code and the token exchange (getOAuthTokenUrl, below) must land on the
  // same backend: gwAppUrl picks the app that talks to the backend admin-web uses.
  const url = gwAppUrl('/login')
  url.searchParams.set('client_id', getOAuthClientId())
  url.searchParams.set('redirect_uri', getOAuthRedirectUri())
  url.searchParams.set('response_type', 'code')
  url.searchParams.set('code_challenge', codeChallenge)
  url.searchParams.set('code_challenge_method', 'S256')
  url.searchParams.set('state', state)

  return url.toString()
}

export function getOAuthRedirectUri(): string {
  return window.location.origin + OAUTH_REDIRECT_PATH
}

export function getStoredCodeVerifier(): string | null {
  return sessionStorage.getItem(SESSION_KEY_CODE_VERIFIER)
}

export function getStoredState(): string | null {
  return sessionStorage.getItem(SESSION_KEY_STATE)
}

// Re-validate against SAFE_PATH on read — sessionStorage is same-origin but
// not tamper-proof against extensions or earlier-loaded scripts.
export function getStoredFrom(): string | null {
  const value = sessionStorage.getItem(SESSION_KEY_FROM)
  if (value === null || !SAFE_PATH.test(value)) {
    return null
  }

  return value
}

export function getProcessedCodeKey(code: string): string {
  return `${SESSION_KEY_PROCESSED_CODE_PREFIX}${code}`
}

export function clearOAuthStorage(): void {
  sessionStorage.removeItem(SESSION_KEY_CODE_VERIFIER)
  sessionStorage.removeItem(SESSION_KEY_STATE)
  sessionStorage.removeItem(SESSION_KEY_FROM)

  const keysToRemove: string[] = []
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i)
    if (key !== null && key.startsWith(SESSION_KEY_PROCESSED_CODE_PREFIX)) {
      keysToRemove.push(key)
    }
  }
  for (const key of keysToRemove) {
    sessionStorage.removeItem(key)
  }
}

// Length-independent comparison closes a timing-channel attacker could
// otherwise use to probe the first mismatching byte of the stored state.
export function timingSafeEqual(a: string, b: string): boolean {
  if (a.length !== b.length) {
    return false
  }
  let diff = 0
  for (let i = 0; i < a.length; i++) {
    diff |= a.charCodeAt(i) ^ b.charCodeAt(i)
  }

  return diff === 0
}

export function getOAuthTokenUrl(): string {
  return `${getApiBaseUrl()}/v4/accounts/oauth2/token/`
}
